import React from 'react';
import { Alert, Box, Select, MenuItem, Typography, Divider } from '@mui/material';
import { TrendingUp as MarkupIcon, LocalOffer as DiscountIcon } from '@mui/icons-material';
import { DecimalNumberInput, FlagSwitch } from '../../../../components/ui';
import * as S from '../../SalesStep2/SalesStep2.styles';

interface LinePricingToolbarProps {
  editable: boolean;
  markupEnabled: boolean;
  allowIndividualMarkups: boolean;
  discountEnabled: boolean;
  allowIndividualDiscounts: boolean;
  markupType: 'PERCENTAGE' | 'AMOUNT';
  markupValue: number;
  discountType: 'PERCENTAGE' | 'AMOUNT';
  discountValue: number;
  maxUniformDiscountAmount: number;
  /** When true, order-level markup has no positive catalog base (all pieces at 0). */
  orderMarkupHasNoCatalogBase?: boolean;
  onMarkupToggle: (enabled: boolean) => void;
  onIndividualMarkupToggle: (enabled: boolean) => void;
  onMarkupTypeChange: (type: 'PERCENTAGE' | 'AMOUNT') => void;
  onMarkupValueChange: (value: number) => void;
  onDiscountToggle: (enabled: boolean) => void;
  onIndividualDiscountToggle: (enabled: boolean) => void;
  onDiscountTypeChange: (type: 'PERCENTAGE' | 'AMOUNT') => void;
  onDiscountValueChange: (value: number) => void;
}

const compactSelectSx = {
  minWidth: 76,
  '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' },
};

const compactInputSx = {
  width: 96,
  '& .MuiInputBase-root': { fontSize: '0.8125rem' },
  '& .MuiFormHelperText-root': { display: 'none' },
};

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap' as const,
};

const uniformControlsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap' as const,
  pl: 3.5,
  pt: 0.5,
};

const LinePricingToolbar: React.FC<LinePricingToolbarProps> = ({
  editable,
  markupEnabled,
  allowIndividualMarkups,
  discountEnabled,
  allowIndividualDiscounts,
  markupType,
  markupValue,
  discountType,
  discountValue,
  maxUniformDiscountAmount,
  orderMarkupHasNoCatalogBase = false,
  onMarkupToggle,
  onIndividualMarkupToggle,
  onMarkupTypeChange,
  onMarkupValueChange,
  onDiscountToggle,
  onIndividualDiscountToggle,
  onDiscountTypeChange,
  onDiscountValueChange,
}) => {
  return (
    <S.ToolbarPanel sx={{ mb: 1.5, p: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={sectionHeaderSx}>
          <MarkupIcon
            color={markupEnabled ? 'success' : 'action'}
            sx={{ fontSize: 18, flexShrink: 0 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            Incremento
          </Typography>
          <FlagSwitch
            checked={markupEnabled}
            onChange={(e, checked) => onMarkupToggle(checked)}
            disabled={!editable}
            size="small"
          />
          {markupEnabled && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Por pieza
              </Typography>
              <FlagSwitch
                checked={allowIndividualMarkups}
                onChange={(e, checked) => onIndividualMarkupToggle(checked)}
                disabled={!editable}
                size="small"
              />
            </>
          )}
        </Box>

        {markupEnabled && !allowIndividualMarkups && (
          <Box sx={uniformControlsSx}>
            <Select
              value={markupType}
              onChange={(e) => onMarkupTypeChange(e.target.value as 'PERCENTAGE' | 'AMOUNT')}
              size="small"
              disabled={!editable}
              sx={compactSelectSx}
            >
              <MenuItem value="PERCENTAGE">%</MenuItem>
              <MenuItem value="AMOUNT">Bs</MenuItem>
            </Select>
            <DecimalNumberInput
              size="small"
              value={markupValue > 0 ? markupValue : ''}
              onChange={(val) => onMarkupValueChange(val ?? 0)}
              decimalPlaces={2}
              min={0}
              disabled={!editable}
              sx={compactInputSx}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Se aplica al total de la orden
            </Typography>
          </Box>
        )}

        {markupEnabled && !allowIndividualMarkups && orderMarkupHasNoCatalogBase && (
          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
            No hay precios de catálogo (&gt; 0) para repartir el incremento de la orden. Las piezas
            siguen en 0; usa incremento por pieza para asignar un monto.
          </Alert>
        )}

        <Divider sx={{ opacity: 0.6 }} />

        <Box sx={sectionHeaderSx}>
          <DiscountIcon
            color={discountEnabled ? 'success' : 'action'}
            sx={{ fontSize: 18, flexShrink: 0 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            Descuento
          </Typography>
          <FlagSwitch
            checked={discountEnabled}
            onChange={(e, checked) => onDiscountToggle(checked)}
            disabled={!editable}
            size="small"
          />
          {discountEnabled && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Por pieza
              </Typography>
              <FlagSwitch
                checked={allowIndividualDiscounts}
                onChange={(e, checked) => onIndividualDiscountToggle(checked)}
                disabled={!editable}
                size="small"
              />
            </>
          )}
        </Box>

        {discountEnabled && !allowIndividualDiscounts && (
          <Box sx={uniformControlsSx}>
            <Select
              value={discountType}
              onChange={(e) => onDiscountTypeChange(e.target.value as 'PERCENTAGE' | 'AMOUNT')}
              size="small"
              disabled={!editable}
              sx={compactSelectSx}
            >
              <MenuItem value="PERCENTAGE">%</MenuItem>
              <MenuItem value="AMOUNT">Bs</MenuItem>
            </Select>
            <DecimalNumberInput
              size="small"
              value={discountValue > 0 ? discountValue : ''}
              onChange={(val) => onDiscountValueChange(val ?? 0)}
              decimalPlaces={2}
              min={0}
              max={discountType === 'PERCENTAGE' ? 99 : maxUniformDiscountAmount}
              disabled={!editable}
              sx={compactInputSx}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Se aplica al total de la orden
            </Typography>
          </Box>
        )}
      </Box>
    </S.ToolbarPanel>
  );
};

export default LinePricingToolbar;
